import React, { forwardRef, type MutableRefObject, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { type CSSProperties } from 'styled-components';

import { ReactComponent as InfoIcon } from '@pkg/assets/icons/info.svg';
import SkullImage from '@pkg/assets/images/Skull_And_Crossbones.png';
import { Tooltip } from '@pkg/components';
import { Switch } from '@pkg/components/Sections/Profile/Switch';
import { type CombinedCollectionMetadata, useBreakpoints } from '@pkg/hooks';

import { type ActivityListEntryType, ActivityStatusTypes } from '@app/client';

import { type ActivityFeedType } from '../../hooks/useDaasActivity';

import { ActivityEntry } from './ActivityEntry';
import { ActivityFilters, type ActivityFilterTabsType } from './ActivityFilters';
import { NewItemsButton } from './NewItemsButton';

import {
    ActiveWallets,
    ActivityDot,
    ActivityToggle,
    Content,
    Description,
    EmptyCopy,
    EmptyStateWrapper,
    ListContainer,
    ListItem,
    ListItemSkeleton,
    ListWrapper,
    LiveStatus,
    SkeletonList,
    StatusWrapper,
    StyledImage,
    Title,
    Wrapper,
} from './styles';

const EVENT_HEIGHT = 90;
const EVENT_HEIGHT_MOBILE = 70;

const isFiltered = (type: ActivityStatusTypes, filter?: ActivityFilterTabsType) => {
    if (!filter || filter === 'All') return false;

    switch (type) {
        case ActivityStatusTypes.Sale:
            return filter !== 'Sales';
        case ActivityStatusTypes.Mint:
            return filter !== 'Mints';
        case ActivityStatusTypes.Listed:
            return filter !== 'Listings';
        case ActivityStatusTypes.Buy:
            return filter !== 'Buys';
        case ActivityStatusTypes.Transfer:
            return filter !== 'Transfers';
        case ActivityStatusTypes.FloorPrice:
            return filter !== 'Floor Price';

        default:
            return true;
    }
};

export type ActivityPanelVariants = 'detail-view' | 'wishlist' | 'collection';
export type ActivityListProps = {
    pastActivity?: ActivityListEntryType[];
    isLoading?: boolean;
    collectionAddresses?: string[];
    activeWalletNumber?: number;
    variant?: ActivityPanelVariants;
} & Pick<ActivityFeedType, 'activityFeed' | 'transitionFeed' | 'newActivityCounter'>;

export const ActivityList = forwardRef<
    HTMLDivElement,
    ActivityListProps & {
        collectionData?: CombinedCollectionMetadata;
        scrollRef: MutableRefObject<HTMLDivElement | null>;
        slug?: string;
    }
>(
    (
        {
            activityFeed,
            newActivityCounter,
            transitionFeed,
            pastActivity = [],
            variant,
            collectionData,
            collectionAddresses,
            scrollRef,
            activeWalletNumber = 0,
            isLoading,
        },
        ref,
    ) => {
        const { t: translate } = useTranslation(['nft_details']);
        const { isMobile } = useBreakpoints();

        const [showCollectionActivity, setShowCollectionActivity] = useState(false);
        const [activeTab, setActiveTab] = useState<ActivityFilterTabsType>(variant !== 'wishlist' ? 'Buys' : 'All');

        const virtualizerRef = useRef<HTMLDivElement>(null);
        useImperativeHandle(ref, () => virtualizerRef.current as HTMLDivElement);

        const filteredItems = activityFeed.filter((item) => {
            if (!item.uuid || !item.image) return false;
            if (showCollectionActivity && !collectionAddresses?.includes(item.contract_address)) return false;
            return !isFiltered(item?.status, activeTab);
        });
        const filteredPastActivity = pastActivity.filter((item) => {
            if (!item.uuid || !item.image) return false;
            if (showCollectionActivity && !collectionAddresses?.includes(item.contract_address)) return false;
            return !isFiltered(item?.status, activeTab);
        });

        const virtualizer = useVirtualizer({
            count: filteredItems.length + filteredPastActivity.length,
            overscan: 20,
            getScrollElement: () => virtualizerRef.current,
            // This value matches what we use in the `.scss`,
            // but ideally comes from a shareable `tokens.ts`.
            estimateSize: () => (isMobile ? EVENT_HEIGHT_MOBILE : EVENT_HEIGHT),
        });

        const listHeight = virtualizer.getTotalSize();
        const itemsMarkup = virtualizer.getVirtualItems().map(({ index, start }) => {
            const item = index < filteredItems.length ? filteredItems[index] : filteredPastActivity[index];
            const style: CSSProperties = {
                position: 'absolute',
                top: 0,
                left: 30,
                transform: `translateY(${start + 10}px)`,
            };

            return (
                item && (
                    <ListItem key={`activity-list-entry-${item?.uuid}`} style={style}>
                        <ActivityEntry data={item} />
                    </ListItem>
                )
            );
        });

        const pastScrollHeight = useRef(listHeight);
        useEffect(() => {
            const currentScroll = virtualizer.scrollOffset;
            if (pastScrollHeight.current !== listHeight && currentScroll > 0) {
                virtualizer.scrollToOffset(currentScroll + (listHeight - pastScrollHeight.current));
            }
            pastScrollHeight.current = listHeight;
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [listHeight]);

        const isEmpty = filteredItems.length + filteredPastActivity.length === 0;
        const renderEmptyState = isEmpty && !isLoading;
        const renderLoadingState = isEmpty && isLoading;

        return (
            <Wrapper>
                <Content $variant={variant}>
                    <Title>
                        {variant === 'wishlist'
                            ? translate('activity_module_header_wishlist')
                            : translate('activity_module_header')}
                        {!isMobile && (
                            <Tooltip trigger={<InfoIcon />} placement="bottom" variant="secondary">
                                {translate(
                                    variant === 'wishlist'
                                        ? 'activity_module_holder_tooltip_wishlist'
                                        : 'activity_module_holder_tooltip_owners',
                                )}
                            </Tooltip>
                        )}
                    </Title>

                    <StatusWrapper>
                        <ActiveWallets>
                            <ActivityDot $isFetching={transitionFeed.length === 0} />
                            {activeWalletNumber > 0 && (
                                <>
                                    {activeWalletNumber} <span>{translate('activity_module_active_wallets')}</span>
                                </>
                            )}
                        </ActiveWallets>

                        <LiveStatus $status={transitionFeed.length === 0 ? 'fetching' : 'active'}>
                            {translate(
                                transitionFeed.length === 0 ? 'activity_status_fetching' : 'activity_status_live',
                            )}
                        </LiveStatus>
                    </StatusWrapper>

                    <Description>
                        {variant === 'wishlist'
                            ? translate('activity_module_feed_description_wishlist')
                            : translate('activity_module_feed_description', { collectionName: collectionData?.name })}
                    </Description>

                    {(variant === 'detail-view' || variant === 'collection') && collectionAddresses && (
                        <ActivityToggle>
                            <Switch
                                id="collection-activity"
                                label={translate(
                                    isMobile
                                        ? 'activity_module_collection_toggle_mobile'
                                        : 'activity_module_collection_toggle',
                                )}
                                value="activity"
                                checked={showCollectionActivity}
                                onCheckedChange={setShowCollectionActivity}
                            />
                        </ActivityToggle>
                    )}

                    <ActivityFilters variant={variant} activeTab={activeTab} setActiveTab={setActiveTab} />
                </Content>

                {/*}
                <TopBuys />

                <TitlePadding>
                    <SubTitle>{activeTab} Activity</SubTitle>
                </TitlePadding>
                {*/}

                <ListWrapper>
                    <ListContainer ref={virtualizerRef} style={{ height: listHeight }}>
                        <AnimatePresence>
                            {newActivityCounter !== 0 && (
                                <NewItemsButton newItemsCount={newActivityCounter} scrollRef={scrollRef} />
                            )}
                        </AnimatePresence>
                        {itemsMarkup}

                        {renderLoadingState && (
                            <SkeletonList>
                                {Array(20)
                                    .fill(null)
                                    .map((_, i) => (
                                        <ListItemSkeleton key={`Activity-Skeleton-${i}`} />
                                    ))}
                            </SkeletonList>
                        )}
                        {renderEmptyState && (
                            <EmptyStateWrapper>
                                <StyledImage src={SkullImage} />
                                <EmptyCopy>{translate('activity_module_empty_state')}</EmptyCopy>
                            </EmptyStateWrapper>
                        )}
                    </ListContainer>
                </ListWrapper>
            </Wrapper>
        );
    },
);

ActivityList.displayName = 'ActivityList';
